const STORAGE_PREFIX = 'imai:workspace-navigation-pending:v1:';
const PENDING_TTL_MS = 2 * 60 * 60_000;

type PendingPrompt = { sourceMessageId: string; expiresAt: number };

export type NavigationStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function getBrowserNavigationStorage(): NavigationStorage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function storageKey(threadId: string): string {
  return `${STORAGE_PREFIX}${threadId}`;
}

function readPending(
  storage: NavigationStorage,
  threadId: string,
  now: number
): PendingPrompt[] {
  try {
    const parsed: unknown = JSON.parse(storage.getItem(storageKey(threadId)) ?? '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is PendingPrompt =>
        typeof item === 'object' &&
        item !== null &&
        typeof item.sourceMessageId === 'string' &&
        item.sourceMessageId.length > 0 &&
        typeof item.expiresAt === 'number' &&
        item.expiresAt > now
    );
  } catch {
    return [];
  }
}

export function rememberPendingNavigationPrompt(
  storage: NavigationStorage,
  threadId: string,
  sourceMessageId: string,
  now = Date.now()
): void {
  try {
    const current = readPending(storage, threadId, now);
    const pending = [
      ...current.filter((entry) => entry.sourceMessageId !== sourceMessageId),
      { sourceMessageId, expiresAt: now + PENDING_TTL_MS },
    ].slice(-10);
    storage.setItem(storageKey(threadId), JSON.stringify(pending));
  } catch {
    // Private browsing can deny storage. The durable card still works.
  }
}

export function consumePendingNavigationPrompt(
  storage: NavigationStorage,
  threadId: string,
  sourceMessageId: string,
  now = Date.now()
): boolean {
  try {
    const current = readPending(storage, threadId, now);
    const hasPending = current.some(
      (entry) => entry.sourceMessageId === sourceMessageId
    );
    if (!hasPending) return false;
    storage.setItem(
      storageKey(threadId),
      JSON.stringify(
        current.filter((entry) => entry.sourceMessageId !== sourceMessageId)
      )
    );
    return true;
  } catch {
    return false;
  }
}
